import { IHeadCell } from '../../../../shared/interfaces/table-display.model';
import { FunctionComponent, SetStateAction, useContext } from 'react';
import { TableDisplay } from '../../miscellaneous-components/table-display';
import { TableActionButtons } from '../../miscellaneous-components/table-action-buttons';
import { IFileUploadLineSupplier } from '../../../../shared/services/file-upload-all-lines/file-upload-all-lines.model';
import { useNavigate } from 'react-router-dom';
import { ScreenLabelsContext } from '../../../../context/screen-labels/screen-labels';
import { EditUploadLineSupplierDialog } from '../../../dialogs/settings/data-import/edit-upload-line-supplier-dialog';
import { Chip, Tooltip, IconButton, Stack } from '@mui/material';
import { EditOutlined } from '@mui/icons-material';

export const CsvUploadSuppliers: FunctionComponent<{
  fileUploadLineSuppliers: IFileUploadLineSupplier[],
  editClickHandler: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>, uploadLine: IFileUploadLineSupplier) => void,
  deleteHandler: (id: string) => void,
  openDialog: boolean,
  setValidatedUploadLines: React.Dispatch<SetStateAction<any>>,
  selected: string[],
  setSelected: React.Dispatch<SetStateAction<any>>,
  editUploadLine: IFileUploadLineSupplier | undefined,
  closeDialogHandler: () => void,
  submitHandler: (lineId: string, editUploadLine: IFileUploadLineSupplier) => void,
  status: 'pending' | 'validated' | 'completed' | 'discarded',
  inputPage: number,
  setInputPage: React.Dispatch<SetStateAction<any>>,
  totalRecords: number,
  perPage: number,
  setPerPage: React.Dispatch<SetStateAction<any>>,
  setOrder: React.Dispatch<SetStateAction<any>>,
  setOrderBy: React.Dispatch<SetStateAction<any>>,
  isSubmitting: boolean,
  errorCode?: number,
  errorMessage?: string,
}> = ({
  fileUploadLineSuppliers,
  editClickHandler,
  deleteHandler,
  openDialog,
  setValidatedUploadLines,
  selected,
  setSelected,
  editUploadLine,
  closeDialogHandler,
  submitHandler,
  status,
  inputPage,
  setInputPage,
  totalRecords,
  perPage,
  setPerPage,
  setOrder,
  setOrderBy,
  isSubmitting,
  errorCode,
  errorMessage,
}) => {
    const navigate = useNavigate();
    //Context
    const screenLabelsContext = useContext(ScreenLabelsContext)

    const mapValidationErrors = (uploadLines: any[]) => {
      return uploadLines.map((uploadLine: any) => {
        const rowNumber = typeof uploadLine.rowNumber === 'number' ? uploadLine.rowNumber : undefined
        const validationErrors = Array.isArray(uploadLine.validationErrors) ? uploadLine.validationErrors : []

        const fieldList = validationErrors.length > 0 ? (
          <Stack spacing={0.5}>
            {validationErrors.map((ve: any, idx: number) => (
              <div key={idx}>{ve.field || 'Unknown'}</div>
            ))}
          </Stack>
        ) : ''

        const errorList = validationErrors.length > 0 ? (
          <Stack spacing={0.5}>
            {validationErrors.map((ve: any, idx: number) => {
              const errorText = ve.message || 'Validation failed'
              const truncatedError = errorText.length > 100 ? errorText.substring(0, 100) + '...' : errorText
              return <div key={idx}>{truncatedError}</div>
            })}
          </Stack>
        ) : ''

        const severityList = validationErrors.length > 0 ? (
          <Stack spacing={0.5}>
            {validationErrors.map((ve: any, idx: number) => (
              <Chip
                key={idx}
                color={ve.severity === 'warning' ? 'warning' : 'error'}
                label={ve.severity === 'warning' ? 'Warning' : 'Error'}
                size="small"
                variant="outlined"
              />
            ))}
          </Stack>
        ) : ''

        return {
          id: uploadLine.id,
          row: rowNumber || '',
          field: fieldList,
          error: errorList,
          severity: severityList,
          action: (
            <Tooltip title="Edit" placement='top'>
              <IconButton
                onClick={() => editClickHandler({ stopPropagation: () => { } } as any, uploadLine)}
                size="small">
                <EditOutlined sx={{ cursor: 'pointer', fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          ),
        }
      })
    }

    const headCells: IHeadCell[] = status === 'pending' ?
      [
        { id: 'action', numeric: false, label: 'Action', sortable: false },
        { id: 'row', numeric: true, label: 'Row', sortable: false },
        { id: 'field', numeric: false, label: 'Field', sortable: false },
        { id: 'error', numeric: false, label: 'Error', sortable: false },
        { id: 'severity', numeric: false, label: 'Severity', sortable: false },
      ]
      :
      [
        ...(status !== 'discarded' ? [{ id: 'action', numeric: false, label: 'Action', sortable: false }] : []),
        { id: 'name', numeric: false, label: screenLabelsContext.getLabel('supplier', { plural: false, capitalize: 'first' }) + ' Name', sortable: true },
        { id: 'contactSupplierReference', numeric: false, label: screenLabelsContext.getLabel('supplier', { plural: false, capitalize: 'first' }) + ' Reference', sortable: true },
        { id: 'verifiers', numeric: false, label: 'Verifiers', sortable: false },
        { id: 'conversionSourceName', numeric: false, label: 'Default Conversion Factor Source', sortable: false },
        { id: 'categoryName', numeric: false, label: 'Default Spend Activity', sortable: false },
        { id: 'primaryContactName', numeric: false, label: 'Primary Contact Name', sortable: true },
        { id: 'primaryContactEmail', numeric: false, label: 'Primary Contact Email', sortable: true },
      ];

    const rows = status === 'pending'
      ? mapValidationErrors(fileUploadLineSuppliers || [])
      : fileUploadLineSuppliers?.map((uploadLine: any) => ({
        id: uploadLine.id,
        name: uploadLine.name,
        contactSupplierReference: uploadLine.contactSupplierReference,
        verifiers: Array.isArray(uploadLine.verifiers) ? uploadLine.verifiers.join(', ') : uploadLine.verifiers,
        conversionSourceName: uploadLine.conversionSourceName,
        categoryName: uploadLine.isAutoClassified ? (
          <Stack direction="row" spacing={1} alignItems="center">
            <span>{uploadLine.categoryName}</span>
            <Tooltip title="Default source and activity matched automatically from Companies House" placement="top">
              <Chip size="small" variant="outlined" color="info" label="Auto-matched" sx={{ height: 20 }} />
            </Tooltip>
          </Stack>
        ) : uploadLine.categoryName,
        primaryContactName: uploadLine.primaryContactName,
        primaryContactEmail: uploadLine.primaryContactEmail,
        action: status === 'validated' ? (
          <TableActionButtons
            id={uploadLine.id}
            includeEdit={true}
            includeDelete={true}
            onEdit={(_id: string) => editClickHandler({ stopPropagation: () => { } } as any, uploadLine)}
            onDelete={(id: string) => deleteHandler(id)}
          />
        ) : status === 'completed' ? (
          <TableActionButtons
            id={uploadLine.id}
            includeView={true}
            onView={() => viewSupplierClickHandler(uploadLine.supplierId)}
            disableButton={!uploadLine.supplierId}
          />
        ) : undefined,
      }));

    const viewSupplierClickHandler = (supplierId?: string) => {
      if (supplierId) {
        navigate(`/organisation/suppliers/${supplierId}`);
      }
    };

    const handleSelected = (selectedRowId: string[]) => {
      const formatted = selectedRowId.map(row => ({ id: row }));
      setValidatedUploadLines(formatted)
    };

    const handleOrderChange = (orderBy: string, order: 'asc' | 'desc') => {
      setOrderBy(orderBy);
      setOrder(order);
    };

    return (
      <>
        <TableDisplay
          headCells={headCells}
          rows={rows}
          selectByIcon={(status === 'validated' || status === 'pending') ? false : true}
          selectByCheckboxOnly={(status === 'validated' || status === 'pending') ? true : false}
          onOrderChange={handleOrderChange}
          handleSelected={handleSelected}
          haveCheckboxSelect={(status === 'validated' || status === 'pending') ? true : false}
          selected={selected}
          setSelected={setSelected}
          inputPage={inputPage}
          setInputPage={setInputPage}
          totalRecords={totalRecords}
          perPage={perPage}
          setPerPage={setPerPage}
        />
        {editUploadLine && openDialog &&
          <EditUploadLineSupplierDialog
            open={openDialog}
            isSubmitting={isSubmitting}
            closeHandler={closeDialogHandler}
            submitHandler={submitHandler}
            editUploadLine={editUploadLine as IFileUploadLineSupplier}
            errorCode={errorCode || 0}
            errorMessage={errorMessage || ''}
          />
        }
      </>
    );
  }


